import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { dashOverview } from './dash-api';
import { mln } from './format';
import { DealModal } from './DealModal';
import { useUser } from './user-context';
import { useCehs } from './cehs';
import { useData } from './useData';

const barClass = (p: number) => (p >= 80 ? 'ok' : p >= 40 ? 'warn' : 'bad');

export function DashScreen() {
  const user = useUser();
  const cehs = useCehs();
  const nav = useNavigate();
  const { data: d, refresh } = useData('dash', dashOverview);
  const [openId, setOpenId] = useState<number | null>(null);
  const go = (screen: string) => user.screens.includes(screen) && nav('/' + screen);

  if (!d) return <p className="muted">Загрузка…</p>;
  const k = d.kpi;
  const maxMonth = Math.max(1, ...d.charts.months.map((m) => m.plan));
  const maxFunnel = Math.max(1, ...d.charts.funnel.map((f) => f.sum));

  const KPI = [
    {
      c: 'gold',
      ico: '📄',
      label: 'Портфель контрактов',
      v: mln(k.portfolio ?? 0) + ' ₸',
      sub: k.active + ' активных сделок',
      to: 'deals',
      fin: true,
    },
    {
      c: 'grn',
      ico: '✅',
      label: 'Получено оплат',
      v: mln(k.received ?? 0) + ' ₸',
      sub: 'по всем договорам',
      to: 'pay',
      fin: true,
    },
    {
      c: 'cy',
      ico: '🧾',
      label: 'Дебиторка',
      v: mln(k.debitor ?? 0) + ' ₸',
      sub: 'к получению',
      to: 'pay',
      fin: true,
    },
    {
      c: 'red',
      ico: '⏰',
      label: 'Просрочка / без счёта',
      v: mln(k.overdue ?? 0) + ' ₸',
      sub: 'требует действий',
      to: 'pay',
      fin: true,
    },
    {
      c: 'vi',
      ico: '🏭',
      label: 'В производстве',
      v: String(k.inProd),
      sub: 'заказов в цехах',
      to: 'prod',
      fin: false,
    },
    {
      c: 'red',
      ico: '🚩',
      label: 'Красных флагов',
      v: String(k.flags),
      sub: 'мешают исполнению',
      to: 'deals',
      fin: false,
    },
  ].filter((x) => !d.financeHidden || !x.fin);

  return (
    <div className="scr">
      <div className="scr-head">
        <h2>Пульт генерального директора</h2>
        <span className="muted">весь завод на одном экране</span>
      </div>

      <div className="kpis">
        {KPI.map((x, i) => (
          <div key={i} className="kpi" style={{ cursor: 'pointer' }} onClick={() => go(x.to)}>
            <div className="kl">
              {x.ico} {x.label}
            </div>
            <div className={'kv ' + x.c}>{x.v}</div>
            <div className="muted small" style={{ marginTop: 4 }}>
              {x.sub}
            </div>
          </div>
        ))}
      </div>

      {!d.financeHidden && (
        <div className="dash-grid">
          <div className="glass pad">
            <div className="section-t" style={{ marginTop: 0 }}>
              Поступления по месяцам <span className="muted small">план vs факт</span>
            </div>
            <div className="chart-months">
              {d.charts.months.map((m, i) => (
                <div
                  key={i}
                  className="cm-col"
                  title={`${m.month}: план ${mln(m.plan)} · факт ${mln(m.fact)}`}
                >
                  <div className="cm-val">{m.fact > 0 ? Math.round(m.fact / 1e6) : '·'}</div>
                  <div className="cm-bars">
                    <div
                      className="cm-plan"
                      style={{ height: Math.round((m.plan / maxMonth) * 120) + 'px' }}
                    />
                    <div
                      className="cm-fact"
                      style={{ height: Math.round((m.fact / maxMonth) * 120) + 'px' }}
                    />
                  </div>
                  <div className={'cm-mo' + (m.current ? ' cur' : '')}>{m.month}</div>
                </div>
              ))}
            </div>
            <div className="muted" style={{ fontSize: 9.5, textAlign: 'right', marginTop: 6 }}>
              млн ₸ · число над столбцом = факт
            </div>
          </div>

          <div className="glass pad">
            <div className="section-t" style={{ marginTop: 0 }}>
              Портфель по этапам <span className="muted small">сумма контрактов</span>
            </div>
            {d.charts.funnel.map((f, i) => (
              <div key={i} className="fbar">
                <div className="fl">{f.stage}</div>
                <div className="ft">
                  <span style={{ width: Math.round((f.sum / maxFunnel) * 100) + '%' }} />
                </div>
                <div className="fv mono">{mln(f.sum)}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="dash-grid">
        <div className="glass pad">
          <div className="section-t" style={{ marginTop: 0 }}>
            ⚠️ Требует внимания
          </div>
          {d.attention.length === 0 && (
            <p className="muted">Красных флагов нет — все контракты в срок.</p>
          )}
          {d.attention.map((a, i) => (
            <div key={i} className={'att ' + a.level}>
              <div className="att-ic">{a.ico}</div>
              <div className="grow">
                <div className="att-t">{a.title}</div>
                <div className="muted small">{a.text}</div>
              </div>
              {a.dealId && (
                <button className="btn ghost sm" onClick={() => setOpenId(a.dealId)}>
                  Открыть →
                </button>
              )}
            </div>
          ))}
        </div>

        <div className="glass pad">
          <div className="section-t" style={{ marginTop: 0 }}>
            Загрузка производства (цеха)
          </div>
          {d.cehLoad.map((c) => (
            <div key={c.id} style={{ marginBottom: 11 }}>
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  fontSize: 12.5,
                  marginBottom: 5,
                }}
              >
                <b>{cehs.full(c.id)}</b>
                <span className="mono muted">{c.avg}%</span>
              </div>
              <div className={'bar ' + barClass(c.avg)}>
                <span style={{ width: c.avg + '%' }} />
              </div>
            </div>
          ))}
        </div>
      </div>

      {openId != null && (
        <DealModal
          dealId={openId}
          screens={user.screens}
          onClose={() => setOpenId(null)}
          onChanged={refresh}
        />
      )}
    </div>
  );
}
